import * as _ from "lodash";

import {
    OptionBasicTextDto,
    LanguageDto,
} from "../../services/Client";
import {IMultiLineValue, lang2flag} from "../../components/MultiLineColumn";

/**
 * OptionBasicTextDto type which can be shown in the front end.
 * The value of each text type must correspond with the attribute
 * name inside the OptionBasicTextDto.
 */
export type OptionBasicTextProp = "shorttext" | "longtext";

/**
 * Create OptionBasicTextDtos for all given languages.
 *
 * @param langList the LanguageDto array to search
 * @param textDtos optional array of templates to copy the texts from
 * @returns an array of new OptionBasicTextDtos
 */
export const createTexts = (langList: LanguageDto[], textDtos?: OptionBasicTextDto[]): OptionBasicTextDto[] => {
    return langList.map((language: LanguageDto) => {
        const newText: OptionBasicTextDto = {
            languageDto: language,
            shorttext: "",
            longtext: "",
            objectId: 0,
            versionId: 0,
        };

        // try to find a text for the current language in one of the templates
        const template = _.find(textDtos, (text: OptionBasicTextDto) => (
            text.languageDto ? text.languageDto.objectId === language.objectId : false
        ));
        if (template !== undefined) {
            if (template.shorttext !== undefined) {
                newText.shorttext = template.shorttext;
            }
            if (template.longtext !== undefined) {
                newText.longtext = template.longtext;
            }
        }
        return newText;
    });
};

/**
 * Select the OptionBasicTextDto from an array with the given language code.
 *
 * @param textDtos the array of OptionBasicTextDto to search
 * @param language the language code or name that we want
 * @returns the OptionBasicTextDto with the given language code or undefined if none found
 */
const findTextDto = (textDtos: OptionBasicTextDto[], language: string): OptionBasicTextDto | undefined => {
    const langStr = language.toLowerCase();
    return _.find(textDtos, value => (
        value.languageDto
            ? (value.languageDto.nameDe.toLowerCase() === langStr ||
               value.languageDto.code.toLowerCase() === langStr)
            : false
    ));
};

/**
 * Select the text value of a given text type from an array of OptionBasicTextDto.
 *
 * @param textDtos the array of OptionBasicTextDto to search
 * @param propName the property name (e.g. 'shorttext') in each text instance
 * @param language the language code or name that we want
 * @returns the text value of the text type or undefined if none found
 */
export const findText = (textDtos: OptionBasicTextDto[],
                         propName: OptionBasicTextProp,
                         language: string): string | undefined => {
    const textDto = findTextDto(textDtos, language);
    if (textDto && propName in textDto) {
        return textDto[propName];
    }
    return undefined;
};

/**
 * Select the language with the given langauge code or name from an array of LanguageDto.
 *
 * @param language the language code or name to search for
 * @param langList the LanguageDto array to search
 * @returns the found LanguageDto or undefined if none found
 */
export const findLanguage = (language: string, langList: LanguageDto[]): LanguageDto | undefined => {
    const langStr = language.toLowerCase();
    return _.find(langList, value => (
        value.code.toLowerCase() === langStr ||
        value.nameDe.toLowerCase() === langStr
    ));
};

/**
 * Create multi line values for a given category of texts.
 *
 * @param texts text array to create multi line values for
 * @param propName the property name (e.g. 'shorttext', 'longtext') in each text instance
 * @param langList list of  known to the system; used to sort the multi line values
 * @returns the created IMultiLineValue array; may be empty
 */
export const createMultiLineValues = (texts: OptionBasicTextDto[], propName: string, langList: LanguageDto[]): IMultiLineValue[] => {
    const values: IMultiLineValue[] = [];
    // add multi line values in the order of the sytem languages
    langList.forEach((language: LanguageDto) => {
        // try to find a text in the given language
        const text = _.find(texts, (dto: OptionBasicTextDto) => (
            dto.languageDto !== undefined && dto.languageDto.objectId === language.objectId
        ));
        if (text && text[propName] !== undefined) {
            values.push({
                caption: language.nameDe,
                text: text[propName],
                flag: lang2flag(language.vmce),
            });
        }
    });
    return values;
};

/**
 * Merge the content of a multi language column into a given array of texts.
 *
 * @param oldTexts the existing option basic texts
 * @param propName the property name of the text to be updated (e.g. 'shorttext')
 * @param newTextValue the (possibly) updated multi line column value
 * @param langList list of languages known to the system; used when creating new OptionBasicTextDtos
 * @returns the updated text array
 */
const mergeOptionBasicText = (oldTexts: OptionBasicTextDto[],
                              propName: string,
                              newTextValue: IMultiLineValue[],
                              langList: LanguageDto[]): OptionBasicTextDto[] => {
    if (!Array.isArray(newTextValue)) {
        console.warn("Invalid new text values. OptionBasicTexts will not be updated.");
        return oldTexts;
    }
    const newTexts: OptionBasicTextDto[] = [];
    newTextValue.forEach((multiLineValue: IMultiLineValue) => {
        const language = multiLineValue.caption;
        if (language) {
            const existingDto = findTextDto(oldTexts, language);
            if (existingDto) {
                // update an existing OptionBasicTextDto with new text value
                newTexts.push(Object.assign({}, existingDto, {[propName]: multiLineValue.text}));
            } else {
                // create a new OptionBasicTextDto for the given multi line value
                const languageDto = findLanguage(language, langList);
                if (languageDto) {
                    newTexts.push({
                        [propName]: multiLineValue.text,
                        languageDto: languageDto,
                        objectId: 0,
                        versionId: 0,
                    });
                } else {
                    console.warn(`Failed to create a new option basic text. Unknown language ${language}`);
                }
            }
        }
    });
    return newTexts;
};

/**
 * Merge the content made to multi language columns into a given array of texts.
 *
 * @param oldTexts the existing option basic texts
 * @param newShorttexts the (possibly) updated shorttext multi line column value
 * @param newLongtexts the (possibly) updated longtext multi line column value
 * @returns the updated text array
 */
export const mergeOptionBasicTexts = (oldTexts: OptionBasicTextDto[],
                                      newShorttexts: IMultiLineValue[],
                                      newLongtexts: IMultiLineValue[],
                                      langList: LanguageDto[]): OptionBasicTextDto[] => {
    const newTexts = mergeOptionBasicText(oldTexts, "shorttext", newShorttexts, langList);
    return mergeOptionBasicText(newTexts, "longtext", newLongtexts, langList);
};



// WEBPACK FOOTER //
// ./src/views/BaseOptionsView/BaseOptionsTextUtils.ts